import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { CreateRequeteDialog } from './CreateRequeteDialog';
import { DemoRequestInfos } from './requetes/DemoRequestInfos';
import { DemoRequestMessages } from './requetes/DemoRequestMessages';
import { BROSSARD_DEMO_REQUESTS, DEMO_CITIZEN, type DemoRequestDetail } from '../data/brossardRequests';
import { useLanguage } from '../contexts/LanguageContext';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, SendIcon, LockIcon, BellIcon, CheckIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
/** Citizen-facing tabs on the left pane. */
type LeftTab = 'infos' | 'messages';
/** Internal tabs on the right pane. */
type RightTab = 'gestion' | 'collaboration' | 'historique';
type Status = 'Nouvelle demande' | 'En Cours' | 'Complété' | 'En Attente';
interface Request {
  id: string;
  subject: string;
  category: string;
  reqId: string;
  location: string;
  date: string;
  status: Status;
  department: string;
  requester: {
    name: string;
    initials: string;
    color: string;
  };
  slaDate: string;
  /** Brossard demo requests carry their full file (infos, history, messages). */
  demo?: DemoRequestDetail;
}
/** The four Brossard demo requests, shown first. */
const DEMO_REQUESTS: Request[] = BROSSARD_DEMO_REQUESTS.map((r, i) => ({
  id: `demo-${i}`,
  subject: r.title.fr,
  category: r.category.fr,
  reqId: `#${r.reqId}`,
  location: r.addressLines[0],
  date: r.createdOn.fr,
  status: 'En Cours',
  department: r.service.fr,
  requester: {
    name: DEMO_CITIZEN.name,
    initials: DEMO_CITIZEN.initials,
    color: 'bg-[#4F46E5]'
  },
  slaDate: r.dueOn.fr,
  demo: r
}));
const MOCK_REQUESTS: Request[] = [...DEMO_REQUESTS, {
  id: '1',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-101',
  location: '1520, 1 ère Rue, Richelieu,...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '2',
  subject: 'Bâtiment',
  category: 'Bâtiment',
  reqId: 'R-100',
  location: '205, Avenue 8e, Richelie...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juillet 2026'
}, {
  id: '3',
  subject: 'Nid-de-poule - trait...',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-99',
  location: '2907, Rang de la Savane, ...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '4',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-98',
  location: '32, Place Rouville-Tessier,...',
  date: '8 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '5',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-97',
  location: '700, Rue Martel, Richelie...',
  date: '4 juin 2026',
  status: 'En Cours',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '9 juin 2026'
}, {
  id: '6',
  subject: 'Plateaux sportifs - t...',
  category: 'Parcs et espaces récréatifs',
  reqId: 'R-96',
  location: '900, Rue des Oblats, Ric...',
  date: '3 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juin 2026'
}, {
  id: '7',
  subject: 'Refoulement égout...',
  category: 'Eau potable et égout',
  reqId: 'R-95',
  location: '192, Rue des Érables, Ric...',
  date: '3 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Éric Robin',
    initials: 'ÉR',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '4 juin 2026'
}];
/** English equivalents for the French mock data and status labels. */
const EN_DICT: Record<string, string> = {
  // Brossard demo requests
  ...Object.fromEntries(BROSSARD_DEMO_REQUESTS.flatMap(r => [[r.title.fr, r.title.en], [r.category.fr, r.category.en], [r.service.fr, r.service.en], [r.createdOn.fr, r.createdOn.en], [r.dueOn.fr, r.dueOn.en]])),
  // Statuses
  'Nouvelle demande': 'New request',
  'En Cours': 'In progress',
  'Complété': 'Completed',
  'En Attente': 'On hold',
  // Subjects & categories
  Divers: 'Miscellaneous',
  Bâtiment: 'Building',
  'Nid-de-poule - trait...': 'Pothole - treat...',
  'Plateaux sportifs - t...': 'Sports facilities - t...',
  'Refoulement égout...': 'Sewer backup...',
  'Voirie et infrastructures routi...': 'Roads and road infrastruc...',
  'Parcs et espaces récréatifs': 'Parks and recreational spaces',
  'Eau potable et égout': 'Drinking water and sewer',
  Taxation: 'Taxation',
  Administration: 'Administration',
  'Lampadaire défectueux': 'Faulty streetlight',
  'Éclairage public': 'Street lighting',
  'Eau potable (qualité)': 'Drinking water (quality)',
  'Feux de circulation': 'Traffic lights',
  // Departments
  'Service Des Travaux Publics': 'Public Works Department',
  // Dates
  '8 juin 2026': 'June 8, 2026',
  '4 juin 2026': 'June 4, 2026',
  '3 juin 2026': 'June 3, 2026',
  '13 juin 2026': 'June 13, 2026',
  '9 juin 2026': 'June 9, 2026',
  '4 juin 2026 ': 'June 4, 2026',
  '8 juillet 2026': 'July 8, 2026'
};
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-100 text-slate-700';
    case 'En Cours':
      return 'bg-blue-100 text-blue-700';
    case 'Complété':
      return 'bg-emerald-100 text-emerald-700';
    case 'En Attente':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En Cours':
      return 'bg-blue-500';
    case 'Complété':
      return 'bg-emerald-500';
    case 'En Attente':
      return 'bg-orange-500';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  reqId: string;
  subject: string;
  category: string;
  position: [number, number];
}[] = [...BROSSARD_DEMO_REQUESTS.map(r => ({
  reqId: `#${r.reqId}`,
  subject: r.title.fr,
  category: r.category.fr,
  position: r.coords
})), {
  reqId: 'R-127',
  subject: 'Taxation',
  category: 'Administration',
  position: [45.4528, -73.4622]
}, {
  reqId: 'R-104',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4468, -73.4698]
}, {
  reqId: 'R-70',
  subject: 'Eau potable (qualité)',
  category: 'Eau potable et égout',
  position: [45.4552, -73.4578]
}, {
  reqId: 'R-103',
  subject: 'Feux de circulation',
  category: 'Éclairage public',
  position: [45.4438, -73.4728]
}, {
  reqId: 'R-57',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4488, -73.4548]
}];
// Custom indigo map marker
const requestMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
export function RequetesGerer() {
  const {
    t,
    lang
  } = useLanguage();
  /** Translates a French mock value to English when the interface is in English. */
  const tr = (s: string) => lang === 'en' ? EN_DICT[s] ?? s : s;
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  const query = normalize(search.trim());
  const filteredRequests = query ? MOCK_REQUESTS.filter(r => normalize(r.location).includes(query) || normalize(r.requester.name).includes(query)) : MOCK_REQUESTS;
  const filteredIds = new Set(filteredRequests.map(r => r.reqId));
  const filteredMarkers = query ? MAP_MARKERS.filter(m => filteredIds.has(m.reqId)) : MAP_MARKERS;
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [leftTab, setLeftTab] = useState<LeftTab>('infos');
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  const [draft, setDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  /** Free-text internal note, kept per request. */
  const [internalNotes, setInternalNotes] = useState<Record<string, string>>({});
  if (selectedRequest) {
    const demo = selectedRequest.demo;
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-5712" data-mp="304h0yori5vv4">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-5713" data-mp="2v77ffk78i4ck">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-5714" data-mp="2qee9it014pyw">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-5715" data-mp="2lgci9ksg9kcc">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-5716" data-mp="2gff0vaf9kubw"></div>
            </div>
            {t('Visible par le citoyen', 'Visible to the citizen')}
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-5717" data-mp="27hd3d4uqojtd">
            <button onClick={() => setLeftTab('infos')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${leftTab === 'infos' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5718" data-mp="22is3x7nba45z">
              {t('Infos', 'Details')}
            </button>
            <button onClick={() => setLeftTab('messages')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors flex items-center gap-2 ${leftTab === 'messages' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5719" data-mp="d7of8oyix1r5">
              {t('Messages', 'Messages')}
              {demo ? demo.messages.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold" data-id="element-5720" data-mp="3vdt370bqz4ju">
                    {demo.messages.length}
                  </span> : <span className="px-1.5 py-0.5 rounded-full bg-[#4F46E5] text-white text-[10px] font-bold" data-id="element-5721" data-mp="1ao3pwrqozqt9">
                  1
                </span>}
            </button>
          </div>

          {leftTab === 'messages' && <>
              {demo ? <div className="p-6 overflow-y-auto flex-1" data-id="element-5722" data-mp="36qzzs26qidlr">
                  <DemoRequestMessages messages={demo.messages} data-id="element-5723" data-mp="3krswmtkidks1" />
                </div> : <div className="p-6 overflow-y-auto flex-1 space-y-5" data-id="element-5724" data-mp="3gpfro7dl5qwb">
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-id="element-5725" data-mp="2t04nsh0xvgdy">
                  <BellIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5726" data-mp="2o236a0j5vp4l" />
                  {t('Nathalie Ouellet reçoit chaque message par courriel et notification bciti+.', 'Nathalie Ouellet receives every message by email and bciti+ notification.')}
                </div>

                <div className="text-center" data-id="element-5727" data-mp="29a1d0zbljudj">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5728" data-mp="25bbz95mncatd">
                    {t('19 mai 2026', 'May 19, 2026')}
                  </span>
                </div>

                {/* Citizen message */}
                <div className="flex gap-3" data-id="element-5729" data-mp="1zzexrbfohsvf">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5730" data-mp="1l8xvjpv7yyxh">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5731" data-mp="2dwk1v5vu0ho1">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5732" data-mp="2dxv1qum5p59v">
                      {t("Bonjour, je voulais savoir si quelqu'un est passé au parc. Les branches sont toujours sur le sentier ce matin et les enfants passent par là pour aller à l'école.", 'Hello, I wanted to know if someone came by the park. The branches are still on the path this morning and the children walk through there to get to school.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5733" data-mp="1drjawtxtkso4">
                      Nathalie Ouellet · {t('19 mai, 08:12', 'May 19, 08:12')}
                    </div>
                  </div>
                </div>

                {/* Staff reply */}
                <div className="flex gap-3 flex-row-reverse" data-id="element-5734" data-mp="z1loojjgad1k">
                  <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5735" data-mp="yz8zblbd50eh">
                    JD
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5736" data-mp="1rnofib68dore">
                    <div className="bg-[#4F46E5] rounded-xl rounded-tr-sm p-3.5 text-sm text-white leading-relaxed" data-id="element-5737" data-mp="1mpmtpihf7bc8">
                      {t('Bonjour Mme Ouellet, merci pour votre signalement. Une équipe des Travaux publics est planifiée pour le ramassage des branches le 21 mai en avant-midi. Les poubelles ont été vidées hier.', 'Hello Ms. Ouellet, thank you for your report. A Public Works crew is scheduled to pick up the branches on the morning of May 21. The garbage bins were emptied yesterday.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 text-right flex items-center justify-end gap-1.5" data-id="element-5738" data-mp="2rwwec0wufmln">
                      Jacques Dubois · {t('19 mai, 09:47', 'May 19, 09:47')}
                      <CheckIcon className="w-3 h-3 text-emerald-500" data-id="element-5739" data-mp="31mpgycf0crdr" />
                      <span className="text-emerald-600 font-medium" data-id="element-5740" data-mp="35p2n31xo2rb2">{t('Lu', 'Read')}</span>
                    </div>
                  </div>
                </div>

                <div className="text-center" data-id="element-5741" data-mp="3amch6uw03r58">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5742" data-mp="3fns3srdtu5pq">
                    {t('20 mai 2026', 'May 20, 2026')}
                  </span>
                </div>

                {/* Automated status message */}
                <div className="flex gap-3" data-id="element-5743" data-mp="1lblhawfhg2i4">
                  <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5744" data-mp="1d81duynvzvue">
                    <BellIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5745" data-mp="1990vtid8gx8g" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5746" data-mp="1n8adynl021f2">
                    <div className="bg-white border border-dashed border-slate-300 rounded-xl p-3.5 text-sm text-slate-600 leading-relaxed" data-id="element-5747" data-mp="1i7cyiirdjlh9">
                      <span className="font-semibold text-slate-700" data-id="element-5748" data-mp="1w6493v712p0n">
                        {t('Mise à jour automatique', 'Automatic update')}
                      </span>
                      <br data-id="element-5749" data-mp="25wfbveez6s5y" />
                      {t('Le statut de votre requête est passé à « En cours ». Échéance prévue : 26 mai 2026.', 'Your request status changed to “In progress”. Expected due date: May 26, 2026.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5750" data-mp="2ay4p90po7hx7">
                      {t('Envoyé automatiquement · 20 mai, 07:00', 'Sent automatically · May 20, 07:00')}
                    </div>
                  </div>
                </div>

                {/* Latest citizen message, unanswered */}
                <div className="flex gap-3" data-id="element-5751" data-mp="2kjxkgnvns6xn">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5752" data-mp="2pj0frglu6ysf">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5753" data-mp="2fkue903xzuo3">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5754" data-mp="2fmnkwfek2ew3">
                      {t('Parfait, merci beaucoup pour le suivi rapide !', 'Perfect, thank you very much for the quick follow-up!')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5755" data-mp="1wtjpaohs1ryu">
                      Nathalie Ouellet · {t('20 mai, 11:23', 'May 20, 11:23')} ·{' '}
                      <span className="text-[#4F46E5] font-medium" data-id="element-5756" data-mp="2ca9vqhmkoe33">
                        {t('Sans réponse', 'Unanswered')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>}

              {/* Composer */}
              <div className="border-t border-slate-200 p-4 bg-white" data-id="element-5757" data-mp="1yf7j7bn7zqhb">
                <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} placeholder={t('Écrire au citoyen...', 'Write to the citizen...')} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5758" data-mp="1tfbd96uypoz4" />
                <div className="flex items-center justify-between mt-2.5" data-id="element-5759" data-mp="1ap3gd6huvurd">
                  <div className="flex items-center gap-1" data-id="element-5760" data-mp="15oxvc963d6i9">
                    <button aria-label={t('Joindre un fichier', 'Attach a file')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-5761" data-mp="vl0o25wdh1ft">
                      <PaperclipIcon className="w-4 h-4" data-id="element-5762" data-mp="15gc2so7s68nk" />
                    </button>
                    <span className="text-xs text-slate-400" data-id="element-5763" data-mp="1agqdke0rnwpa">
                      {t('Visible par le citoyen', 'Visible to the citizen')}
                    </span>
                  </div>
                  <button disabled={!draft.trim()} onClick={() => setDraft('')} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-semibold rounded-lg hover:bg-[#4338CA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5764" data-mp="htcby2m4icyu">
                    <SendIcon className="w-4 h-4" data-id="element-5765" data-mp="3v77cb4lpfrsf" />
                    {t('Envoyer', 'Send')}
                  </button>
                </div>
              </div>
            </>}

          <div className={`p-6 overflow-y-auto flex-1 ${leftTab === 'infos' ? '' : 'hidden'}`} data-id="element-5766" data-mp="1w9ihk3pygs5i">
            {demo ? <DemoRequestInfos request={demo} statusBadge={<div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 flex-shrink-0 ${getStatusColor(selectedRequest.status)}`} data-id="element-5768" data-mp="2595law65qa1q">
                    <div className={`w-2 h-2 rounded-full ${getStatusDot(selectedRequest.status)}`} data-id="element-5769" data-mp="1dk2hfhi6n1pm"></div>
                    {tr(selectedRequest.status)}
                  </div>} data-id="element-5767" data-mp="21a6h5ds92hbc" /> : <>
            <div className="flex justify-between items-start mb-8" data-id="element-5770" data-mp="1mdfix7ubbbnb">
              <div className="flex gap-4" data-id="element-5771" data-mp="pt6r6otdkekh">
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5772" data-mp="tv1s3s7e8pxq">
                  <FolderIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-5773" data-mp="mahexz5fzjhk" />
                </div>
                <div data-id="element-5774" data-mp="hcft56gmt62e">
                  <div className="flex items-center gap-2" data-id="element-5775" data-mp="hd7i3yfq45jp">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-5776" data-mp="dakuyatzz8su">
                      {t('Entretien des parcs', 'Park maintenance')}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-5777" data-mp="8ecaqj0y0pet" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-5778" data-mp="3urkmwkc4f4wa">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200" data-id="element-5779" data-mp="3uprb6qmnhuhf">
                      R-71
                    </span>
                    <span data-id="element-5780" data-mp="3lsh3ff91knh9">{tr('Parcs et espaces récréatifs')}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-5781" data-mp="3gqhza35h9mj9">
                    {tr('Service Des Travaux Publics')}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor('En Cours')}`} data-id="element-5782" data-mp="32772xbrprmld">
                <div className={`w-2 h-2 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5783" data-mp="2s7zosmd5nnx7"></div>
                {tr('En Cours')}
              </div>
            </div>

            <div className="mb-8" data-id="element-5784" data-mp="3kwn4m2f4xl3d">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5785" data-mp="3kwdm0zwf6g7p">
                {t('DESCRIPTION', 'DESCRIPTION')}
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-5786" data-mp="3b4r64q94zsym">
                {t("Le parc près de la rue Principale nécessite un entretien urgent. Les poubelles débordent et il y a des branches cassées sur le sentier principal suite à la tempête d'hier soir. Cela représente un danger pour les piétons et les cyclistes.", "The park near Principale Street needs urgent maintenance. The garbage bins are overflowing and there are broken branches on the main path following last night's storm. This is a hazard for pedestrians and cyclists.")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-5787" data-mp="21wnwtc8g5yv9">
              <div data-id="element-5788" data-mp="21za3l9nikacf">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5789" data-mp="1e738sexfsbf4">
                  {t('DEMANDEUR', 'REQUESTER')}
                </h3>
                <div className="flex items-start gap-3" data-id="element-5790" data-mp="19acx03so01zi">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-5791" data-mp="1n92sxg3qzvma">
                    NO
                  </div>
                  <div data-id="element-5792" data-mp="2ym5aoehl2r81">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-5793" data-mp="2ylltx13o68vl">
                      Nathalie Ouellet
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-5794" data-mp="3pvrpey84ynfv">
                      (450) 658-1157
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-5795" data-mp="3tyei11cmsce4">
                      adjointe.tp@ville.richelieu.qc.ca
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-5796" data-mp="7r6l8nfs1dtp">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5797" data-mp="7rg9o6l6bcxa">
                  {t('EMPLACEMENT', 'LOCATION')}
                </h3>
                <div className="flex items-start gap-2" data-id="element-5798" data-mp="gq1nxpjm45s9">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5799" data-mp="gogu6254gqvg" />
                  <div data-id="element-5800" data-mp="ljmonpclbdv5">
                    <div className="text-sm text-slate-700" data-id="element-5801" data-mp="qgd54dsb4bh3">
                      200 Boulevard Richelieu
                    </div>
                    <div className="text-sm text-slate-700" data-id="element-5802" data-mp="vgseyvddr0ub">
                      Richelieu, J3L 3R1
                    </div>
                    <button className="text-sm text-[#4F46E5] mt-1 hover:underline" data-id="element-5803" data-mp="10f2aj730o3gg">
                      {t('Voir sur la carte ▾', 'View on map ▾')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-5804" data-mp="2axvsovk7g3si">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5805" data-mp="2azgm2hus9gos">
                {t('PIÈCES JOINTES', 'ATTACHMENTS')}
              </h3>
              <div className="flex gap-3" data-id="element-5806" data-mp="33nvyqxqfzmwy">
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5807" data-mp="33mt5ij5nd8dv">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5808" data-mp="2ypkmyhgl72c3" />
                </div>
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5809" data-mp="3mdkgfp5a96ud">
                  <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5810" data-mp="3hn409xa2v74b" />
                </div>
                <button className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5811" data-mp="1ko55e3vize25">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5812" data-mp="1aws8uyys7zps" />
                  <span className="text-xs font-medium" data-id="element-5813" data-mp="wy107s2ov691">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
            </>}
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-5814" data-mp="n392mo93wje9">
          <button onClick={() => setSelectedRequest(null)} aria-label={t('Fermer', 'Close')} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-5815" data-mp="s0sj7uta189d">
            <XIcon className="w-5 h-5" data-id="element-5816" data-mp="apvkq7bos4m0" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-5817" data-mp="aoc42fm4brjp">
            <div className="flex items-center gap-2" data-id="element-5818" data-mp="kgzvsvy8r5bk">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-5819" data-mp="kikvezzexega">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-5820" data-mp="fnx1glln3rub"></div>
              </div>
              {t('Interne — non visible par le citoyen', 'Internal — not visible to the citizen')}
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-5821" data-mp="13fc98tw6r1cy" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-5822" data-mp="13ekpqhfv4u31">
            {[{
            id: 'gestion' as const,
            label: t('Gestion', 'Management')
          }, {
            id: 'collaboration' as const,
            label: t('Collaboration', 'Collaboration')
          }, {
            id: 'historique' as const,
            label: t('Historique', 'History')
          }].map(tab => <button key={tab.id} onClick={() => setRightTab(tab.id)} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === tab.id ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5823" data-mp="2ecm8uo37m94d">
                {tab.label}
              </button>)}
          </div>

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-5824" data-mp="25e0kudeyy1w7">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5825" data-mp="25fv5dqmbvrzr">
                <div className="flex items-center justify-between gap-3 mb-3" data-id="element-5826" data-mp="29gx2a0srvryk">
                  <label htmlFor="request-internal-note" className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5827" data-mp="3gs5o147cy7qh">
                    {t('NOTE INTERNE', 'INTERNAL NOTE')}
                  </label>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400" data-id="element-5828" data-mp="15525rtit5ez7">
                    <LockIcon className="w-3 h-3" data-id="element-5829" data-mp="10e298pdloyx8" />
                    {t('Jamais visible par le citoyen', 'Never visible to the citizen')}
                  </span>
                </div>
                <textarea id="request-internal-note" rows={4} value={internalNotes[selectedRequest?.id ?? ''] ?? ''} onChange={e => {
              const key = selectedRequest?.id ?? '';
              const value = e.target.value;
              setInternalNotes(prev => ({
                ...prev,
                [key]: value
              }));
            }} placeholder={t('Contexte, consignes ou informations utiles pour l’équipe…', 'Context, instructions or useful information for the team…')} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 resize-y focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5830" data-mp="qgfuc2wax7d9" />
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5831" data-mp="35wde5biqwuhv">
                <div className="flex items-center justify-between gap-3 mb-4" data-id="element-5832" data-mp="31wuyau5s79lw">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5833" data-mp="2e70jw85tdczj">
                    {t('COMMENTAIRES INTERNES', 'INTERNAL COMMENTS')}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400" data-id="element-5834" data-mp="2n76vfohyc2w2">
                    <LockIcon className="w-3 h-3" data-id="element-5835" data-mp="2i7ayuabl3dqj" />
                    {t('Jamais visible par le citoyen', 'Never visible to the citizen')}
                  </span>
                </div>
                <div className="space-y-3" data-id="element-5836" data-mp="3tn9380vthblf">
                  <div className="flex gap-3" data-id="element-5837" data-mp="3tm7va5a3l57x">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5838" data-mp="5ym1eke5y9cn">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-5839" data-mp="3s63pcp7stl4c">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5840" data-mp="3s755h83o8xsx">
                        {t("Visite sur place effectuée. Trois branches de bonne taille à tronçonner, la nacelle n'est pas nécessaire. Prévoir deux employés pour l'avant-midi.", 'Site visit done. Three large branches to cut up, the bucket truck is not needed. Plan two workers for the morning.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5841" data-mp="jw24pn15xn06">
                        Jacques Dubois · {t('il y a 2 h', '2 h ago')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3" data-id="element-5842" data-mp="1maeldf2vjwvn">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5843" data-mp="1m93m9skdjdav">
                      MG
                    </div>
                    <div className="flex-1" data-id="element-5844" data-mp="1caxn3ipycl9p">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5845" data-mp="18andzx6v5eqm">
                        {t("Le camion nacelle est en réparation jusqu'au 22. On peut quand même y aller au sol comme prévu le 21.", 'The bucket truck is under repair until the 22nd. We can still work from the ground as planned on the 21st.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5846" data-mp="oohb24ynexus">
                        Martin Gauthier · {t('hier, 16:04', 'yesterday, 16:04')}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-5847" data-mp="2r9uq7cjyf38g">
                  <input type="text" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder={t('Ajouter un commentaire interne...', 'Add an internal comment...')} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5848" data-mp="2rbflj4e3eq7d" />
                  <button disabled={!noteDraft.trim()} onClick={() => setNoteDraft('')} className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5849" data-mp="2clgr9cpnryx6">
                    {t('Publier', 'Post')}
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-5850" data-mp="c00yjq0zawp">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5851" data-mp="3rhrodbcf49ft">
                {t("JOURNAL D'ACTIVITÉ", 'ACTIVITY LOG')}
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-5852" data-mp="95d4sgpmnwxk">
                <div className="relative pl-6" data-id="element-5853" data-mp="4biozod5823r">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5854" data-mp="j16nyixuqoob"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5855" data-mp="e1trr7l3c592">
                    {t('Intervention planifiée au 21 mai', 'Intervention scheduled for May 21')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5856" data-mp="o1ug57xthbk4">
                    Martin Gauthier ({t('Parcs et espaces verts', 'Parks and green spaces')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5857" data-mp="2psxqrx7piq4o">
                    {t('20 mai 2026 · 16:04', 'May 20, 2026 · 16:04')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5858" data-mp="2jpkqdp8d531k">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5859" data-mp="2jpb1y62yv3xz"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5860" data-mp="25wbjbv7w0tm7">
                    {t('Réponse envoyée au citoyen', 'Reply sent to the citizen')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5861" data-mp="1vyx5m3dcmixl">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5862" data-mp="1n0bswouk5y4q">
                    {t('19 mai 2026 · 09:47', 'May 19, 2026 · 09:47')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5863" data-mp="3hd1l925a2za0">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-white" data-id="element-5864" data-mp="3hecon0uta50l"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5865" data-mp="3mabi32uzyd7v">
                    {t('Statut changé de « Nouvelle demande » à « En cours »', 'Status changed from “New request” to “In progress”')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5866" data-mp="3awt1k3tggyjw">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5867" data-mp="360kt199rk3lp">
                    {t('19 mai 2026 · 09:30', 'May 19, 2026 · 09:30')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5868" data-mp="2iaxed6my35k">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-5869" data-mp="3tvndz65g5mww"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5870" data-mp="3owawl0xch81p">
                    {t('Requête assignée aux Travaux publics', 'Request assigned to Public Works')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5871" data-mp="lflmx62gti7t">
                    Sylvie Tremblay ({t('Réception', 'Front desk')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5872" data-mp="xqcwhl567dgs">
                    {t('19 mai 2026 · 08:41', 'May 19, 2026 · 08:41')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5873" data-mp="12s28p444px6g">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-5874" data-mp="17ptzfut1fd92"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5875" data-mp="1bq4679onm7oz">
                    {t('Requête déposée avec deux photos', 'Request submitted with two photos')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5876" data-mp="1lgpczkeheiiz">
                    Nathalie Ouellet ({t('Application bciti+', 'bciti+ app')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5877" data-mp="1vf690vsa1nbw">
                    {t('19 mai 2026 · 08:12', 'May 19, 2026 · 08:12')}
                  </div>
                </div>
              </div>
            </div>}

          <div className={`p-6 overflow-y-auto flex-1 space-y-6 ${rightTab === 'gestion' ? '' : 'hidden'}`} data-id="element-5878" data-mp="2kpc7bxt8l3r3">
            <div className="text-sm text-slate-500 font-medium" data-id="element-5879" data-mp="2up3280lplkrp">
              {t('19 mai', 'May 19')} <span className="mx-2 text-slate-300" data-id="element-5880" data-mp="2umqndwb8zqiv">→</span>{' '}
              <span className="text-red-500" data-id="element-5881" data-mp="2prbab6l2dyni">{t('26 mai', 'May 26')}</span>
            </div>

            {/* Tasks */}
            <div className="space-y-3" data-id="element-5882" data-mp="220p2ngez2ge1">
              <div className="flex items-start gap-3" data-id="element-5883" data-mp="1x1m5ei58hegm">
                <CheckSquareIcon className="w-5 h-5 text-slate-900 mt-0.5" data-id="element-5884" data-mp="1x35p6iq3m7n5" />
                <div className="flex-1" data-id="element-5885" data-mp="2br0hvz43wc08">
                  <div className="text-sm text-slate-500 line-through" data-id="element-5886" data-mp="2bs1y0hzzboot">
                    {t('Vérifier la disponibilité des équipes', 'Check crew availability')}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5887" data-mp="1j7ux1d38o0mh">
                    {t('Terminée : 22 mai 2026', 'Completed: May 22, 2026')}{' '}
                    <PencilIcon className="w-3 h-3 inline ml-1" data-id="element-5888" data-mp="3pxy7o8fajikj" />
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5889" data-mp="3upyduilyf7pk">
                  <CalendarIcon className="w-3 h-3" data-id="element-5890" data-mp="3gy1ounfl1dxe" /> {t('22 mai 2026', 'May 22, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5891" data-mp="3kybu1xvjw0b7">
                  ML
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5892" data-mp="himzhokwcbrr">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5893" data-mp="hg0pzjeg3m6w" />
                <div className="flex-1" data-id="element-5894" data-mp="3i3qof8lx1oj">
                  <div className="text-sm text-slate-700" data-id="element-5895" data-mp="8hgeb27aycs6">
                    {t('Planifier le ramassage des branches', 'Schedule branch pickup')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5896" data-mp="2t7xqauh944d9">
                  <CalendarIcon className="w-3 h-3" data-id="element-5897" data-mp="2t5uprgih2sua" /> {t('25 mai 2026', 'May 25, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5898" data-mp="2czcputzzizvd">
                  ST
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5899" data-mp="2316oqem05xrk">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5900" data-mp="282w3a48fotke" />
                <div className="flex-1" data-id="element-5901" data-mp="2vr5l6v2j0x69">
                  <div className="text-sm text-slate-700" data-id="element-5902" data-mp="2vs88yu4jyjhy">
                    {t('Contacter le demandeur pour suivi', 'Contact the requester for follow-up')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5903" data-mp="2lt9ag4di448o">
                  <CalendarIcon className="w-3 h-3" data-id="element-5904" data-mp="2qrasclz6xxik" /> {t('20 mai 2026', 'May 20, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center text-[10px]" data-id="element-5905" data-mp="1acsrw26nh7hl">
                  <UserIcon className="w-3 h-3" data-id="element-5906" data-mp="3tky8airrwc9q" />
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-2" data-id="element-5907" data-mp="3om3lz6ulk5kv">
                <PlusIcon className="w-4 h-4" data-id="element-5908" data-mp="6gs2fpsv0g75" /> {t('Ajouter une tâche', 'Add a task')}
              </button>
            </div>

            {/* Linked Requests */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5909" data-mp="2d2gz97heeqc">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5910" data-mp="3auyj7vvxmx6b">
                {t('REQUÊTES LIÉES', 'LINKED REQUESTS')}
              </h3>
              <div className="space-y-3" data-id="element-5911" data-mp="3jo242p8wqcaz">
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5912" data-mp="3jov4l8h9w5ko">
                  <div className="flex items-center gap-3" data-id="element-5913" data-mp="2r56cbzodso75">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5914" data-mp="2n5nr12sneb3s">
                      {t('Liée ▾', 'Related ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5915" data-mp="v33112x59klp">
                      R-68 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5916" data-mp="14t4l7jmdmivc">
                      {tr('Lampadaire défectueux')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Attente')}`} data-id="element-5917" data-mp="77gsb8p4qiok">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Attente')}`} data-id="element-5918" data-mp="h28n64rburfn"></div>
                    {tr('En Attente')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5919" data-mp="1sl334mgbzchg">
                  <div className="flex items-center gap-3" data-id="element-5920" data-mp="3hysx0y4jeibc">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5921" data-mp="3d0hk2eiy3row">
                      {t('Bloque ▾', 'Blocks ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5922" data-mp="33bpyco4ha3mf">
                      R-72 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5923" data-mp="2uarr7wyqtfzm">
                      {t('Nid-de-poule rue Principale', 'Pothole on Principale Street')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Cours')}`} data-id="element-5924" data-mp="2kclr9kwhyjxe">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5925" data-mp="nhplel39oat7"></div>
                    {tr('En Cours')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5926" data-mp="3geafu89b7sr8">
                  <div className="flex items-center gap-3" data-id="element-5927" data-mp="32gvm4rc0wytg">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5928" data-mp="37ckm2vryghqa">
                      {t('Doublon ▾', 'Duplicate ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5929" data-mp="2xh95e7wzl0fw">
                      R-75 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5930" data-mp="2oofmm0ibzixa">
                      {t('Banc de parc endommagé', 'Damaged park bench')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Cours')}`} data-id="element-5931" data-mp="2epgdyhxkfnaa">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5932" data-mp="1r231lopnpfsl"></div>
                    {tr('En Cours')}
                  </div>
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-3" data-id="element-5933" data-mp="201q5ch5uyxot">
                <PlusIcon className="w-4 h-4" data-id="element-5934" data-mp="2e0fwzh9ws55s" /> {t('Lier une requête', 'Link a request')}
              </button>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5935" data-mp="2nsc0j7urknhe">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5936" data-mp="2it96eigbo1o7">
                {t('ÉTIQUETTES', 'TAGS')}
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-5937" data-mp="2xqhzphp6rfik">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5938" data-mp="2srovygzpb2vl">
                  {t('Urgent', 'Urgent')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5939" data-mp="34tg6sizveyhu" />
                </span>
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5940" data-mp="34r3ou5u44o4s">
                  {t('Suivi requis', 'Follow-up needed')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5941" data-mp="3el2omy6prvtk" />
                </span>
                <button className="px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 text-xs font-medium flex items-center gap-1 hover:border-slate-400 hover:text-slate-700 transition-colors" data-id="element-5942" data-mp="3ji2w9dj7p6ms">
                  <PlusIcon className="w-3 h-3" data-id="element-5943" data-mp="3sh7v6kyor1zh" /> {t('Ajouter une étiquette', 'Add a tag')}
                </button>
              </div>
            </div>

            {/* Internal Files */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5944" data-mp="1y7479yfk8087">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5945" data-mp="234mf1lhuc9em">
                {t('FICHIERS INTERNES', 'INTERNAL FILES')}
              </h3>
              <div className="flex gap-3" data-id="element-5946" data-mp="1qtlhfodnih2l">
                <div className="w-20 rounded-lg border border-slate-200 p-2 flex flex-col items-center justify-center text-center" data-id="element-5947" data-mp="1qrs5btk9r4n7">
                  <FileTextIcon className="w-8 h-8 text-red-500 mb-2" data-id="element-5948" data-mp="1gvxj6ogu6rfo" />
                  <div className="text-[10px] font-medium text-slate-700 truncate w-full" data-id="element-5949" data-mp="1gtuinai25fwp">
                    rapport_inspe...
                  </div>
                  <div className="text-[9px] text-slate-400" data-id="element-5950" data-mp="29le7b9xfbm63">{t('22 mai', 'May 22')}</div>
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5951" data-mp="24jykpdfll7ll">
                  <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5952" data-mp="1zl577l0b9glf" />
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5953" data-mp="1dv5fngmxw6zn">
                  <img src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5954" data-mp="1dx8dgmuc34ex" />
                </div>
                <button className="w-20 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5955" data-mp="18wu6l84gy0ih">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5956" data-mp="21jyfvpalh102" />
                  <span className="text-xs font-medium" data-id="element-5957" data-mp="1ntv0nia4u7k4">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-5958" data-mp="2fk95yq42ybk6">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-5959" data-mp="2kc10tcu9r3g4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-5960" data-mp="8w8v36izg141">
          <div className="flex items-center gap-3" data-id="element-5961" data-mp="3x5pnkz3bsvj">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-5962" data-mp="3we658irpllm">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5963" data-mp="il0jj4ou43a7">
                <TableIcon className="w-4 h-4" data-id="element-5964" data-mp="3h4zae4giyjez" />
                {t('Tableau', 'Table')}
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5965" data-mp="3w2j9wvunjmdr">
                <MapIcon className="w-4 h-4" data-id="element-5966" data-mp="16bd1z9hphif1" />
                {t('Carte', 'Map')}
              </button>
            </div>
            <span className="text-sm text-slate-500" aria-live="polite" data-id="element-5967" data-mp="1kvuu9ykey58b">
              {query ? t(`${filteredRequests.length} résultat${filteredRequests.length > 1 ? 's' : ''}`, `${filteredRequests.length} result${filteredRequests.length > 1 ? 's' : ''}`) : t(`${MOCK_REQUESTS.length} demandes`, `${MOCK_REQUESTS.length} requests`)}
            </span>
          </div>

          <div className="flex items-center gap-3" data-id="element-5968" data-mp="1yq5lcmjvubhj">
            <div className="relative" data-id="element-5969" data-mp="1tynlk1tligy5">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5970" data-mp="1txkrjl0z7ye0" />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Adresse ou demandeur…', 'Address or requester…')} aria-label={t('Rechercher par adresse ou demandeur', 'Search by address or requester')} className="pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5971" data-mp="1pwsk8xbo03kd" />
              {search && <button type="button" onClick={() => setSearch('')} aria-label={t('Effacer la recherche', 'Clear search')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-700" data-id="element-5972" data-mp="i5ljtivskfst">
                  <XIcon className="w-3.5 h-3.5" data-id="element-5973" data-mp="wtziriq37k43" />
                </button>}
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-5974" data-mp="10wmhm3l6e5as">
              <FilterIcon className="w-4 h-4" data-id="element-5975" data-mp="1au0u5s3za9xt" />
              {t('Filtrer', 'Filter')}
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-5976" data-mp="36wvsvcens4yg">
              <PlusIcon className="w-4 h-4" data-id="element-5977" data-mp="3gpbbmbxs4xke" />
              {t('Créer une requête', 'Create a request')}
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-5978" data-mp="3lmbjmse6wae5">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-5979" data-mp="3lm1sh1herx6v">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-5980" data-mp="2581w20hrupod" />
            {filteredMarkers.map(m => <Marker key={m.reqId} position={m.position} icon={requestMarkerIcon} data-id="element-5981" data-mp="frg29uy5ql3e">
                <Popup data-id="element-5982" data-mp="61ec8xm8uslu">
                  <div className="text-sm" data-id="element-5983" data-mp="61wgohj28d4u">
                    <div className="font-semibold text-slate-900" data-id="element-5984" data-mp="5z1ykwnir4el">
                      {m.reqId} — {tr(m.subject)}
                    </div>
                    <div className="text-slate-500" data-id="element-5985" data-mp="3t5b0aqa9nntt">{tr(m.category)}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-5986" data-mp="3j8ps5w2y9wlr">
        <table className="w-full text-left border-collapse" data-id="element-5987" data-mp="3ja0xw1fyhefi">
          <thead data-id="element-5988" data-mp="3o7iz15nqepcy">
            <tr className="border-b border-slate-200 bg-white" data-id="element-5989" data-mp="3o6zpac88j7a0">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5990" data-mp="1hhcrjj80mavi">
                <span className="inline-flex items-center gap-1" data-id="element-5991" data-mp="1mfmkdn69ozdy">
                  {t('Statut', 'Status')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5992" data-mp="3ih7xviv71gpd" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5993" data-mp="3ehpb0hjtavjw">
                <span className="inline-flex items-center gap-1" data-id="element-5994" data-mp="39fzz6zormdur">
                  {t('Sujet', 'Subject')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5995" data-mp="34kue2336t37q" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5996" data-mp="2usygqu8xdh4k">
                <span className="inline-flex items-center gap-1" data-id="element-5997" data-mp="2qrcwuoxz42kv">
                  {t('Catégorie', 'Category')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5998" data-mp="2lss4tcsvkp7i" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5999" data-mp="2gs46e62bh639">
                <span className="inline-flex items-center gap-1" data-id="element-6000" data-mp="2bv3xlne31p8g">
                  {t('Département', 'Department')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6001" data-mp="21pm5im7upflc" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6002" data-mp="26rbkgcy72dep">
                <span className="inline-flex items-center gap-1" data-id="element-6003" data-mp="2bm8pz9w9m4mu">
                  {t('ID Requête', 'Request ID')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6004" data-mp="1izo3onunciq5" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6005" data-mp="1ny7oum1f1qhg">
                {t('Emplacement', 'Location')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6006" data-mp="1svzf7bmex4jj">
                <span className="inline-flex items-center gap-1" data-id="element-6007" data-mp="1xqn3b9di7wxl">
                  {t('Date de création', 'Created on')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-6008" data-mp="157zr6jghjs8n" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6009" data-mp="x6s1snrj1bsp">
                {t('Demandeur', 'Requester')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-6010" data-mp="s6metkw7cdgy">
                {t('Date SLA', 'SLA date')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-6011" data-mp="120mjjlnkngp5">
            {filteredRequests.length === 0 && <tr data-id="element-6012" data-mp="99mct2xuvyts">
                <td colSpan={9} className="px-4 py-12 text-center" data-id="element-6013" data-mp="9a4ieq6erped">
                  <SearchIcon className="w-6 h-6 text-slate-300 mx-auto mb-2" data-id="element-6014" data-mp="9bz446pi7lji" />
                  <p className="text-sm font-semibold text-slate-700" data-id="element-6015" data-mp="o86mr2l9kewo">
                    {t('Aucune requête trouvée', 'No request found')}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5" data-id="element-6016" data-mp="1zl93bxnd54gu">
                    {t('Vérifiez l’adresse ou le nom du demandeur.', 'Check the address or the requester’s name.')}
                  </p>
                </td>
              </tr>}
            {filteredRequests.map(req => <tr key={req.id} onClick={() => setSelectedRequest(req)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-6017" data-mp="1t5s5vjj8zp4c">
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-6018" data-mp="2kwy7k4em9uvq">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`} data-id="element-6019" data-mp="2kxhrtrrqovcx">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(req.status)}`} data-id="element-6020" data-mp="2c3v14g2e2ial"></div>
                    {tr(req.status)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-6021" data-mp="33uizxlglbbl3">
                  <div className="flex items-center gap-2" data-id="element-6022" data-mp="38o3oyo07gv85">
                    <div className="w-8 h-8 rounded bg-slate-50 border border-slate-200 flex items-center justify-center" data-id="element-6023" data-mp="2jwekb3dwlo1z">
                      <FolderIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-6024" data-mp="2f44udncsgnzp" />
                    </div>
                    {tr(req.subject)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6025" data-mp="257ibh440w70b">
                  {tr(req.category)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6026" data-mp="32tz8s9crakm2">
                  {tr(req.department)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6027" data-mp="2swb2qn9p9qol">
                  {req.reqId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6028" data-mp="2nwon676efycm">
                  <div className="flex items-center gap-1" data-id="element-6029" data-mp="1hhnzspvfwohn">
                    {req.location}
                    <MapPinIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-6030" data-mp="1ci1myhjixa9d" />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6031" data-mp="3o6pp8nwri3k9">
                  {tr(req.date)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6032" data-mp="3t2oml102ivwt">
                  <div className="flex items-center gap-2" data-id="element-6033" data-mp="1x59btieyol7">
                    <div className={`w-6 h-6 rounded-full ${req.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-6034" data-mp="6pg82t1f44c6">
                      {req.requester.initials}
                    </div>
                    {req.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-6035" data-mp="kmv26b2m90ah">
                  {tr(req.slaDate)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-6036" data-mp="2revyvo0mzt1t">
        <div className="flex items-center gap-2" data-id="element-6037" data-mp="zf4gdukl6zed">
          {t('Éléments par page', 'Items per page')}
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-6038" data-mp="14e7ip796mrin">
            <option data-id="element-6039" data-mp="18dgdmffykvfs">10</option>
            <option data-id="element-6040" data-mp="1d5ga1xwtl67m">20</option>
            <option data-id="element-6041" data-mp="1d5pyhh27v5b7">50</option>
          </select>
        </div>
        <div data-id="element-6042" data-mp="1i4jfhjgppeg4">1 - 10 / 98</div>
        <div className="flex items-center gap-4" data-id="element-6043" data-mp="1i5cd9uxp0tm4">
          <button className="hover:text-slate-900" aria-label={t('Page précédente', 'Previous page')} data-id="element-6044" data-mp="1i6ni7y2vk7et">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-6045" data-mp="1n5qkjargzzj3" />
          </button>
          <button className="hover:text-slate-900" aria-label={t('Page suivante', 'Next page')} data-id="element-6046" data-mp="1s41udlhrma1b">
            <ChevronRightIcon className="w-4 h-4" data-id="element-6047" data-mp="1w311ncqyyivx" />
          </button>
        </div>
      </div>

      {showCreate && <CreateRequeteDialog onClose={() => setShowCreate(false)} data-id="element-6048" data-mp="3tvwwpai4t26v" />}
    </div>;
}
/** Lowercase, accent-free text for search matching. */
function normalize(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
// Missing icons for the component
function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6049" data-mp="3gn64kgc01v04">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" data-id="element-6050" data-mp="86t5qjo3vt4i" />
      <line x1="16" x2="16" y1="2" y2="6" data-id="element-6051" data-mp="84goy9u33ot1" />
      <line x1="8" x2="8" y1="2" y2="6" data-id="element-6052" data-mp="44g8ykrc75jv" />
      <line x1="3" x2="21" y1="10" y2="10" data-id="element-6053" data-mp="43eppt063cr2" />
    </svg>;
}
function UserIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6054" data-mp="3vdlsw3zppj2i">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-6055" data-mp="2ldsqsdihdsj0" />
      <circle cx="12" cy="7" r="4" data-id="element-6056" data-mp="2qcvx01a764sk" />
    </svg>;
}
function PencilIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6057" data-mp="2cbjizvelkd9b">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" data-id="element-6058" data-mp="27dhzj9d9ebxb" />
      <path d="m15 5 4 4" data-id="element-6059" data-mp="27et3paam9loy" />
    </svg>;
}